import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { useResource } from '../lib/useResource';
import type { DeviceDetail } from '../lib/types';
import {
  COMMAND_LABEL,
  COMMAND_STATUS_LABEL,
  CONTROL_LABEL,
  PROFILE_LABEL,
  bytes,
  dateTime,
  relativeTime,
} from '../lib/labels';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Table,
  errorMessage,
} from '../components/ui';
import { ConnectivityBadge, ProfilePair } from './DevicesPage';

const ALL_CONTROLS = Object.keys(CONTROL_LABEL);

function Row({ k, v }: { k: string; v: ReactNode }) {
  return (
    <>
      <dt className="text-slate-500">{k}</dt>
      <dd className="text-slate-900">{v}</dd>
    </>
  );
}

export function DeviceDetailPage() {
  const { id = '' } = useParams();
  const { tenant, can } = useSession();
  const device = useResource(
    () => api.get<DeviceDetail>(`/v1/devices/${id}`, { tenant: true }),
    [tenant?.id, id],
  );
  const [sending, setSending] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const send = async (type: 'PING' | 'REFRESH_INVENTORY') => {
    setSending(type);
    setActionError(null);
    try {
      // Chave gerada por clique: um duplo clique ou reenvio da mesma requisição não duplica o comando.
      await api.post(
        `/v1/devices/${id}/commands`,
        { type, idempotencyKey: `${type}-${crypto.randomUUID()}` },
        { tenant: true },
      );
      await device.reload();
    } catch (e) {
      setActionError(errorMessage(e));
    } finally {
      setSending(null);
    }
  };

  if (device.error) return <Alert tone="error">{errorMessage(device.error)}</Alert>;
  const d = device.data;
  if (!d) return <p className="text-sm text-slate-500">Carregando…</p>;
  const active = d.status === 'ACTIVE';
  const applied = new Set(d.appliedControls);

  return (
    <>
      <PageHeader
        title={d.name}
        description={
          <span className="inline-flex flex-wrap items-center gap-2">
            <ConnectivityBadge c={d.connectivity} /> último contato {relativeTime(d.lastSeenAt)} ·{' '}
            <Link to="/dispositivos" className="text-indigo-700 hover:underline">
              voltar à lista
            </Link>
          </span>
        }
        actions={
          <Button variant="secondary" onClick={() => void device.reload()} busy={device.loading}>
            Atualizar
          </Button>
        }
      />
      {!active && (
        <div className="mb-6">
          <Alert tone="warning">
            Desvinculado {relativeTime(d.revokedAt)}
            {d.revokedReason === 'unenrolled_by_device_user'
              ? ' pelo usuário no próprio aparelho.'
              : '.'}
          </Alert>
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Perfil e controles">
          <div className="space-y-4 text-sm">
            <ProfilePair requested={d.requestedProfile} effective={d.effectiveProfile} />
            {d.requestedProfile !== d.effectiveProfile && (
              <Alert tone="info">
                O aparelho opera como <strong>{PROFILE_LABEL[d.effectiveProfile]}</strong>: os
                recursos de {PROFILE_LABEL[d.requestedProfile]} ainda não estão ativos nele.
              </Alert>
            )}
            <ul className="space-y-1" aria-label="Controles">
              {ALL_CONTROLS.map((c) => (
                <li key={c} className="flex items-center justify-between gap-2">
                  <span className={applied.has(c) ? 'text-slate-900' : 'text-slate-400'}>
                    {CONTROL_LABEL[c]}
                  </span>
                  {applied.has(c) ? (
                    <Badge tone="emerald">Aplicado</Badge>
                  ) : (
                    <Badge>Não aplicado</Badge>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-500">
              Controles exclusivos de Device Owner (desinstalação, quiosque, restrições, instalação
              silenciosa) só aparecem como aplicados em aparelhos provisionados oficialmente.
            </p>
          </div>
        </Card>

        <Card title="Aparelho">
          <dl className="grid grid-cols-[10rem_1fr] gap-y-2 text-sm">
            <Row k="Fabricante / modelo" v={`${d.manufacturer} ${d.model}`} />
            <Row k="Android" v={`${d.androidRelease} (API ${d.sdkInt})`} />
            <Row k="Patch de segurança" v={d.securityPatch ?? '—'} />
            <Row k="Versão do agente" v={d.agentVersion} />
            <Row
              k="Bateria"
              v={
                d.telemetry.batteryPercent != null
                  ? `${d.telemetry.batteryPercent}%${d.telemetry.charging ? ' (carregando)' : ''}`
                  : '—'
              }
            />
            <Row
              k="Armazenamento livre"
              v={
                d.telemetry.storageTotalBytes
                  ? `${bytes(d.telemetry.storageFreeBytes)} de ${bytes(d.telemetry.storageTotalBytes)}`
                  : '—'
              }
            />
            <Row k="Rede" v={d.telemetry.network ?? '—'} />
            <Row k="Pareado em" v={dateTime.format(new Date(d.enrolledAt))} />
            <Row k="Grupo" v={d.group?.name ?? '—'} />
          </dl>
        </Card>

        <Card title="Permissões e consentimento">
          <dl className="grid grid-cols-[10rem_1fr] gap-y-2 text-sm">
            <Row
              k="Notificações"
              v={
                { granted: 'Permitidas', denied: 'Negadas', not_required: 'Não exigidas' }[
                  d.permissions.notifications
                ] ?? d.permissions.notifications
              }
            />
            <Row
              k="Otimização de bateria"
              v={
                d.permissions.batteryOptimizationIgnored
                  ? 'Isento (heartbeat mais regular)'
                  : 'Ativa (heartbeat pode atrasar)'
              }
            />
            <Row k="Localização" v="Não coletada" />
            <Row
              k="Consentimento"
              v={`${d.consentVersion} · ${dateTime.format(new Date(d.consentAt))}`}
            />
          </dl>
        </Card>

        <Card title="Comandos">
          <div className="space-y-4">
            {actionError && <Alert tone="error">{actionError}</Alert>}
            {active && can('commands:send') && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  busy={sending === 'PING'}
                  onClick={() => void send('PING')}
                >
                  {COMMAND_LABEL.PING}
                </Button>
                <Button
                  variant="secondary"
                  busy={sending === 'REFRESH_INVENTORY'}
                  onClick={() => void send('REFRESH_INVENTORY')}
                >
                  {COMMAND_LABEL.REFRESH_INVENTORY}
                </Button>
              </div>
            )}
            <p className="text-xs text-slate-500">
              Os comandos são entregues no próximo contato do agente (até ~15 min; o Android pode
              atrasar em economia de bateria).
            </p>
            <Table
              head={['Comando', 'Situação', 'Criado', 'Concluído']}
              empty={d.commands.length === 0 && <EmptyState>Nenhum comando.</EmptyState>}
            >
              {d.commands.map((c) => (
                <tr key={c.id}>
                  <td className="px-4 py-2">{COMMAND_LABEL[c.type] ?? c.type}</td>
                  <td className="px-4 py-2">
                    <Badge
                      tone={
                        c.status === 'SUCCEEDED'
                          ? 'emerald'
                          : c.status === 'FAILED'
                            ? 'red'
                            : c.status === 'EXPIRED' || c.status === 'CANCELED'
                              ? 'slate'
                              : 'amber'
                      }
                    >
                      {COMMAND_STATUS_LABEL[c.status]}
                    </Badge>
                    {c.error && <div className="text-xs text-red-700">{c.error}</div>}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-slate-500">
                    {relativeTime(c.createdAt)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-slate-500">
                    {relativeTime(c.completedAt)}
                  </td>
                </tr>
              ))}
            </Table>
          </div>
        </Card>

        {active && can('devices:manage') && (
          <Card title="Desvincular" className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <p className="text-slate-600">
                O aparelho deixa de ser aceito pela plataforma e os comandos pendentes são
                cancelados. O app permanece instalado e mostra ao usuário que foi desvinculado.
              </p>
              {confirmRevoke ? (
                <>
                  <Button
                    variant="danger"
                    onClick={async () => {
                      try {
                        await api.post(
                          `/v1/devices/${id}/revoke`,
                          { confirm: true },
                          { tenant: true },
                        );
                        await device.reload();
                      } catch (e) {
                        setActionError(errorMessage(e));
                      }
                      setConfirmRevoke(false);
                    }}
                  >
                    Confirmar desvinculação
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirmRevoke(false)}>
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button variant="secondary" onClick={() => setConfirmRevoke(true)}>
                  Desvincular dispositivo
                </Button>
              )}
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
